import { useState, useEffect } from 'react';

const RoseGarden = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [currentImage, setCurrentImage] = useState(0);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    const element = document.getElementById('rose-garden');
    if (element) observer.observe(element);

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (isVisible) {
      const interval = setInterval(() => {
        setCurrentImage((prev) => (prev + 1) % roseImages.length);
      }, 4000);
      return () => clearInterval(interval);
    }
  }, [isVisible]);

  const roseImages = [
    {
      url: 'https://readdy.ai/api/search-image?query=Panoramic%20view%20of%20beautiful%20rose%20garden%20in%20Indian%20farm%2C%20rows%20of%20colorful%20roses%20in%20full%20bloom%2C%20red%20pink%20white%20yellow%20roses%2C%20peaceful%20garden%20pathways%2C%20morning%20sunlight%20filtering%20through%20petals%2C%20romantic%20floral%20landscape&width=800&height=500&seq=rose-garden-1&orientation=landscape',
      caption: 'Morning Glory in the Rose Garden'
    },
    {
      url: 'https://readdy.ai/api/search-image?query=Close-up%20of%20vibrant%20red%20roses%20with%20dewdrops%2C%20fresh%20morning%20dew%20on%20rose%20petals%2C%20natural%20beauty%20and%20fragrance%2C%20organic%20rose%20cultivation%2C%20pristine%20flower%20garden%2C%20romantic%20floral%20setting%20with%20soft%20lighting&width=800&height=500&seq=rose-garden-2&orientation=landscape',
      caption: 'Dewdrops on Fresh Roses'
    },
    {
      url: 'https://readdy.ai/api/search-image?query=Visitors%20walking%20through%20rose%20garden%20pathways%2C%20families%20enjoying%20floral%20beauty%2C%20people%20smelling%20roses%20and%20taking%20photos%2C%20peaceful%20garden%20experience%2C%20natural%20aromatherapy%2C%20romantic%20garden%20stroll&width=800&height=500&seq=rose-garden-3&orientation=landscape',
      caption: 'Visitors Enjoying the Garden'
    }
  ];

  const roseVarieties = [
    { name: 'Heritage Roses', color: 'text-red-500', icon: 'ri-heart-fill' },
    { name: 'Fragrant Blooms', color: 'text-pink-500', icon: 'ri-flower-line' },
    { name: 'Climbing Roses', color: 'text-rose-500', icon: 'ri-plant-line' },
    { name: 'Garden Roses', color: 'text-red-400', icon: 'ri-leaf-line' }
  ];

  return (
    <section className="py-20 relative overflow-hidden">
      {/* Background with floating petals effect */}
      <div className="absolute inset-0 bg-gradient-to-b from-rose-50 to-pink-50">
        {/* Animated floating petals */}
        {[...Array(6)].map((_, i) => (
          <div
            key={i}
            className="absolute animate-float opacity-20"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animationDelay: `${i * 2}s`,
              animationDuration: `${8 + Math.random() * 4}s`
            }}
          >
            <i className="ri-flower-line text-2xl text-rose-400"></i>
          </div>
        ))}
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className={`text-center mb-16 transform transition-all duration-1000 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'}`}>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-6">
            Rose Garden
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8">
            A sanctuary of natural beauty where every bloom tells a story of love and care
          </p>

          {/* Inspirational Quote */}
          <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 shadow-lg max-w-2xl mx-auto">
            <div className="w-12 h-12 bg-rose-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="ri-double-quotes-l text-2xl text-rose-600"></i>
            </div>
            <blockquote className="text-xl md:text-2xl font-medium text-gray-800 italic">
              "Every flower blooms to share its fragrance — just like our farm."
            </blockquote>
          </div>
        </div>

        {/* Main Garden Display */}
        <div className={`mb-16 transform transition-all duration-1000 delay-200 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'}`}>
          <div className="relative rounded-3xl overflow-hidden shadow-2xl max-w-5xl mx-auto">
            <div className="relative h-96 md:h-[500px]">
              {roseImages.map((image, index) => (
                <div
                  key={index}
                  className={`absolute inset-0 transition-opacity duration-1000 ${currentImage === index ? 'opacity-100' : 'opacity-0'
                    }`}
                >
                  <img
                    src={image.url}
                    alt={image.caption}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent"></div>
                </div>
              ))}

              {/* Image Caption */}
              <div className="absolute bottom-6 left-6 right-6">
                <div className="bg-white/90 backdrop-blur-sm rounded-xl p-4">
                  <h3 className="text-xl font-semibold text-gray-800">
                    {roseImages[currentImage].caption}
                  </h3>
                </div>
              </div>

              {/* Navigation Dots */}
              <div className="absolute bottom-6 right-6 flex space-x-2">
                {roseImages.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentImage(index)}
                    className={`w-3 h-3 rounded-full transition-all duration-300 cursor-pointer ${currentImage === index ? 'bg-white' : 'bg-white/50'
                      }`}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Rose Varieties */}
        <div className={`grid grid-cols-2 md:grid-cols-4 gap-6 mb-16 transform transition-all duration-1000 delay-400 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'}`}>
          {roseVarieties.map((variety, index) => (
            <div
              key={index}
              className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 text-center shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2"
            >
              <div className={`w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-br from-rose-100 to-pink-100 flex items-center justify-center ${variety.color}`}>
                <i className={`${variety.icon} text-2xl`}></i>
              </div>
              <h3 className="text-lg font-semibold text-gray-800">{variety.name}</h3>
            </div>
          ))}
        </div>

        {/* Garden Experience */}
        {/* <div className={`bg-gradient-to-r from-rose-500 to-pink-500 rounded-3xl p-8 md:p-12 text-white text-center transform transition-all duration-1000 delay-600 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'}`}>
          <div className="max-w-3xl mx-auto">
            <h3 className="text-3xl md:text-4xl font-bold mb-6">
              Immerse Yourself in Natural Beauty
            </h3>
            <p className="text-xl mb-8 text-rose-100">
              Walk through our carefully tended rose garden, breathe in the natural aromatherapy, and capture memories among the blooms
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button className="bg-white text-rose-600 hover:bg-rose-50 px-8 py-4 rounded-full text-lg font-semibold transition-all duration-300 transform hover:scale-105 whitespace-nowrap cursor-pointer">
                Schedule Garden Tour
              </button>
              <button className="border-2 border-white text-white hover:bg-white hover:text-rose-600 px-8 py-4 rounded-full text-lg font-semibold transition-all duration-300 transform hover:scale-105 whitespace-nowrap cursor-pointer">
                Photography Sessions
              </button>
            </div>
          </div>
        </div> */}
      </div>

      <style jsx>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px) rotate(0deg); }
          33% { transform: translateY(-20px) rotate(120deg); }
          66% { transform: translateY(-10px) rotate(240deg); }
        }
        .animate-float {
          animation: float 8s ease-in-out infinite;
        }
      `}</style>
    </section>
  );
};

export default RoseGarden;